import {
  Alert,
  Button,
  Checkbox,
  Flex,
  Image,
  Input,
  LoadingOverlay,
  Modal,
  Table,
  Text,
  TextInput,
  Title,
  rem,
} from "@mantine/core";
import { BatchRuleActionType, OrderStatus, OrderStatusLabel, User, assets, colors } from "../../constants";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ROUTES } from "../../constants/routes";
import styled from "styled-components";
import { useAppData } from "../../contexts/AppContext";
import moment from "moment";
import { highlightedText } from "../../modules/highlightedText";
import axios from "axios";
import { addSubcollectionDoc, getDocsByQuery } from "../../repositories/firebase";
import { COLLECTIONS, formatPhoneNumber } from "../../constants/global";
import { db } from "../../firebase";
import { collection, getDocs, Timestamp } from "firebase/firestore";
import { addDoc, orderBy, query, where, doc, updateDoc, getDoc, startAfter, limit, documentId } from "@firebase/firestore";
import { usePatients } from "../../hooks/usePatients";
import { notifications } from "@mantine/notifications";
import { invalidatePharmetikaPendingOrdersCache } from "../../repositories/pharmetikaPendingOrders";

type ExistingOrder = { id: string; status: string };

const getRxKey = (datum: any) => String(datum?.rxNumber ?? "").trim();

const getUniqueRxNumbers = (rows: any[]) => Array.from(new Set(rows.map(getRxKey).filter(Boolean)));

const describeExistingOrders = (orders: ExistingOrder[] = []) =>
  orders.map((order) => OrderStatusLabel[order.status] || order.status || "Unknown").join(", ");

const SHIPPING_RULE_ACTIONS: string[] = [
  BatchRuleActionType.Overnight,
  BatchRuleActionType.MustUseUPS,
  BatchRuleActionType.UPS2ndDayAirShipping,
];

const sendSms = ({ uid, oid, rxNumber, drName, phone, name, sender }) => {
  const BASE_URL =
    process.env.REACT_APP_ENV === "prod"
      ? "https://rx2text.com/"
      : "http://localhost:3000";

  let params = `oid=${oid}&rxNumber=${rxNumber}`;
  let link = `${BASE_URL}?${params}`;
  console.log("SMS link:", link);
  const cloudFunctionUrl =
    "https://us-central1-art-of-med-text-to-order.cloudfunctions.net/app/sendWhippySMS";

  let message = `Hey ${name}! This is The Art of Medicine Compounding Pharmacy. It looks like ${drName} prescribed you a medication. For easy and secure ordering, click here: ${link}`;
  axios
    .post(cloudFunctionUrl, {
      data: {
        phoneNumber: phone,
        message: message,
      },
    })
    .then((response) => {
      console.info("SMS sent successfully", response.data);
      addSubcollectionDoc(
        COLLECTIONS.ORDERS,
        oid,
        "order-logs",
        {
          title: `Initial Message.`,
          description: `Message sent to ${name} to complete their order.`,
          sentBy: sender,
        },
        {
          createdAt: true,
        }
      );
    })
    .catch((error: any) => {
      console.error("Error sending SMS:", error);
    });
};

function BatchPreview() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [batchPreview, setBatchPreview] = useState<any[]>([]);

  const {
    batchPreviewData: { saveBatchPreview, getBatchPreview, loading },
    patientsData: { savePatient, getPatients },
    ordersData: { saveOrder, getOrders, orders: _orders },
    updatePendingOrdersCount,
    adminsData,
    user,
  } = useAppData();
  const { getDateString } = usePatients();
  const _batchPreview = location.state?.data || [];
  const ordersToArchive = location.state?.ordersToArchive || [];
  const fromProcessPending = location.state?.fromProcessPending || false;

  const update = location.state?.update || false;
  const currentAdmin = adminsData.admins.find((admin) => admin.email === user?.email);
  const senderInfo = {
    userId: user?.uid || "",
    email: user?.email || "",
    name: currentAdmin?.name || user?.displayName || user?.email || "Unknown",
    source: "admin-ui",
  };


  const fetchAllUsersInChunks = async (batchSize: number = 1000) => {
    const usersRef = collection(db, "users");
    const allDocs: any[] = [];
    let lastDoc: any = null;

    while (true) {
      const q = lastDoc
        ? query(usersRef, orderBy(documentId()), startAfter(lastDoc), limit(batchSize))
        : query(usersRef, orderBy(documentId()), limit(batchSize));
      const snap = await getDocs(q);
      allDocs.push(...snap.docs);
      if (snap.size < batchSize) break;
      lastDoc = snap.docs[snap.docs.length - 1];
    }

    return allDocs;
  };

  // Rx numbers that already have a non-archived order, so processing the same Rx again
  // doesn't create a duplicate order and text the patient a second link
  const getRxNumbersWithActiveOrders = async (rxNumbers: string[]): Promise<Map<string, ExistingOrder[]>> => {
    const existing = new Map<string, ExistingOrder[]>();
    for (let i = 0; i < rxNumbers.length; i += 30) {
      const chunk = rxNumbers.slice(i, i + 30);
      const snapshot = await getDocs(query(collection(db, "orders"), where("rxNumber", "in", chunk)));
      snapshot.forEach((orderDoc) => {
        const order = orderDoc.data();
        const status = order.currentStatus?.status;
        if (status !== OrderStatus.Archived) {
          const rx = getRxKey(order);
          existing.set(rx, [...(existing.get(rx) || []), { id: orderDoc.id, status }]);
        }
      });
    }
    return existing;
  };

  const [existingOrders, setExistingOrders] = useState<Map<string, ExistingOrder[]>>(new Map());
  const [existingOrdersCheck, setExistingOrdersCheck] = useState<"idle" | "checking" | "done" | "failed">("idle");

  // Look up existing orders as soon as the preview opens so the notice shows before upload
  useEffect(() => {
    const rows = location.state?.data || [];
    if (update || rows.length === 0) return;
    let cancelled = false;
    setExistingOrdersCheck("checking");
    getRxNumbersWithActiveOrders(getUniqueRxNumbers(rows))
      .then((found) => {
        if (cancelled) return;
        setExistingOrders(found);
        setExistingOrdersCheck("done");
      })
      .catch((error) => {
        console.error("Error checking for existing orders:", error);
        if (!cancelled) setExistingOrdersCheck("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [location.state]);

  // Rx numbers that appear on more than one row of this batch
  const duplicateRxCounts = useMemo(() => {
    const counts = new Map<string, number>();
    batchPreview.forEach((datum) => {
      const rx = getRxKey(datum);
      if (rx) counts.set(rx, (counts.get(rx) || 0) + 1);
    });
    return new Map(Array.from(counts).filter(([, count]) => count > 1));
  }, [batchPreview]);

  useEffect(() => {
    setBatchPreview(_batchPreview);
  }, [location.state]);

  useEffect(() => {
    setOpen(true);
  }, []);
  const handleUpload = async () => {
    setUploading(true);
    //debugger;
    let rowsToSave = batchPreview;
    if (update) {
      const ordersPromises = _orders.map((order: any) => {
        let updatedResult = batchPreview.find(
          (r) => r.rxNumber === order.rxNumber
        );
        let medications = order.medications.map((o) => {
          if (o.itemName === updatedResult.med) {
            return {
              ...o,
              rxNumber: updatedResult.rxNumber,
              isAutoRefillVisible:
                updatedResult?.metadata[BatchRuleActionType.AutoRefill] ||
                false,
              alt_price: updatedResult.alt_price,
              alt_qty: updatedResult.alt_qty,
              overnight:
                updatedResult?.metadata[BatchRuleActionType.Overnight] ||
                updatedResult?.metadata[
                BatchRuleActionType.OvernightUPS33Shipping
                ] ||
                false,
              metadata: updatedResult?.metadata || {},
            };
          } else return o;
        });
        return saveOrder(
          {
            ...order,
            medications: medications,
            metadata: {
              ...updatedResult?.metadata,
            },
          },
          { refetch: false }
        );
      });

      await Promise.all(ordersPromises);
    } else {
      let rxNumbersWithOrders: Map<string, ExistingOrder[]>;
      try {
        rxNumbersWithOrders = await getRxNumbersWithActiveOrders(getUniqueRxNumbers(batchPreview));
      } catch (error) {
        console.error("Error checking for existing orders:", error);
        notifications.show({
          title: "Upload stopped",
          message: "Could not check for existing orders, so nothing was uploaded. Please try again.",
          color: "red",
        });
        setUploading(false);
        return;
      }

      // Skip Rx numbers that already have an order, and keep only the first row of an Rx repeated in this batch
      const seenRxNumbers = new Set<string>();
      const skippedExisting = new Set<string>();
      const skippedDuplicates = new Set<string>();
      rowsToSave = batchPreview.filter((datum) => {
        const rx = getRxKey(datum);
        const label = `${datum.rxNumber} (${datum.firstName} ${datum.lastName})`;
        if (rxNumbersWithOrders.has(rx)) {
          skippedExisting.add(label);
          return false;
        }
        if (seenRxNumbers.has(rx)) {
          skippedDuplicates.add(label);
          return false;
        }
        seenRxNumbers.add(rx);
        return true;
      });
      if (skippedExisting.size > 0 || skippedDuplicates.size > 0) {
        const messages = [];
        if (skippedExisting.size > 0) {
          messages.push(
            `Already have an order, so no new order or text was sent: ${Array.from(skippedExisting).join(", ")}. ` +
            "To upload one anyway, go to the dashboard (Order Status), archive that Rx, then upload again."
          );
        }
        if (skippedDuplicates.size > 0) {
          messages.push(`Listed more than once in this batch, only the first row was uploaded: ${Array.from(skippedDuplicates).join(", ")}.`);
        }
        notifications.show({
          title: "Some rows were skipped",
          message: messages.join(" "),
          color: "yellow",
          autoClose: false,
        });
      }

      const uniqueIdentifiers = new Set();
      const uniquePatients = rowsToSave.filter((patient) => {
        const dob = getDateString(patient.dateOfBirth);
        // Normalize phone number for unique key generation
        const normalizedPhone = formatPhoneNumber(patient.phone);
        const uniqueKey = `${patient.firstName}|${patient.lastName}|${dob}|${normalizedPhone}`;
        if (!uniqueIdentifiers.has(uniqueKey)) {
          uniqueIdentifiers.add(uniqueKey);
          return true;
        }
        return false;
      });

      // Check for existing patients and create new ones if needed
      const existingPatients = rowsToSave.length > 0 ? await fetchAllUsersInChunks(1000) : [];
      console.log("users count", existingPatients.length);
      const newPatients = [];
      const savedPatients = [];

      for (const patient of uniquePatients) {
        const dob = getDateString(patient.dateOfBirth);
        // Normalize phone number for comparison
        const normalizedPhone = formatPhoneNumber(patient.phone);
        const existingPatientSnapshot = existingPatients.find((doc) => {
          const docData = doc.data();
          const docDateOfBirth = getDateString(docData.dateOfBirth);
          // Normalize BOTH phones before comparison to handle different formats
          const docNormalizedPhone = formatPhoneNumber(docData.phone);
          // Case-insensitive name comparison to handle "KAREN" vs "Karen" differences
          return (
            docData.firstName?.toLowerCase().trim() === patient.firstName?.toLowerCase().trim() &&
            docData.lastName?.toLowerCase().trim() === patient.lastName?.toLowerCase().trim() &&
            docDateOfBirth === dob &&
            docNormalizedPhone === normalizedPhone
          );
        });

        if (existingPatientSnapshot) {
          savedPatients.push({
            ...existingPatientSnapshot.data(),
            id: existingPatientSnapshot.id,
            doctor: patient.doctor,
            key: `${patient.firstName}|${patient.lastName}|${dob}|${normalizedPhone}`,
          });
        } else {
          newPatients.push({
            firstName: patient.firstName,
            lastName: patient.lastName,
            dateOfBirth: patient.dateOfBirth,
            dobString: patient?.dobString,
            phone: normalizedPhone,
            species: patient.species,
            doctor: patient.doctor,
          });
        }
      }

      // Save new patients
      const newPatientPromises = newPatients.map(patient => savePatient(patient, { refetch: false }));
      const newSavedPatients = await Promise.all(newPatientPromises);

      // Combine existing and new saved patients
      savedPatients.push(...newSavedPatients);

      let ordersMap = {};

      rowsToSave.forEach((datum) => {
        const dob = getDateString(datum.dateOfBirth);
        // Normalize phone number for unique key to match savedPatients keys
        const normalizedPhone = formatPhoneNumber(datum.phone);
        const uniqueKey = `${datum.firstName}|${datum.lastName}|${dob}|${normalizedPhone}`;
        let patientSaved = savedPatients.find(
          (patient) => patient.key === uniqueKey
        );

        // Create a new medication object from the current datum
        const newMedication = {
          itemName: datum.med,
          price: datum.price,
          quantity: datum.qty,
          dosageForm: datum.dosage_form,
          rxNumber: datum.rxNumber,

          alt_price: datum?.alt_price,
          alt_qty: datum?.alt_qty,
          isAutoRefillVisible:
            datum?.metadata[BatchRuleActionType.AutoRefill] || false,
          overnight:
            datum?.metadata[BatchRuleActionType.Overnight] ||
            datum?.metadata[BatchRuleActionType.OvernightUPS33Shipping] ||
            false,
          metadata: datum?.metadata || {},
        };

        let reqSignature = false;
        if (datum?.metadata[BatchRuleActionType.RequireSignature]) {
          reqSignature = datum?.metadata[BatchRuleActionType.RequireSignature];
        }

        if (ordersMap[uniqueKey]) {
          // If the order already exists, push the new medication to its medications array
          ordersMap[uniqueKey].medications.push(newMedication);
          ordersMap[uniqueKey].metadata[BatchRuleActionType.RequireSignature] =
            reqSignature;
        } else {
          // Otherwise, create a new order and add it to the orders map
          ordersMap[uniqueKey] = {
            userId: patientSaved?.id || "",
            currentStatus: {
              status: datum.orderStatus,
              date: datum.orderDate,
            },
            orderDate: datum.orderDate,
            //! TODO: remove  isAutoRefillVisible from here
            isAutoRefillVisible:
              datum?.metadata[BatchRuleActionType.AutoRefill] || false,
            medications: [newMedication],
            rxNumber: datum.rxNumber,
            createdAt: new Date(),
            linkSentAt: null, // Will be set when SMS is actually sent
            linkLastSentAt: null,
            linkSendCount: 0,
            metadata: datum.metadata || {},
            doctor: datum.doctor, // starting from now 09-10-24 use the doctor name from the order collection and not from user collection
            // since we are stopping duplicate patients from insertion earlier this was getting stored in user collection
            // so now saving the doctor details to order collection makes more sense
          };
        }
      });

      // Convert the ordersMap back to an array to match the expected Order[] type
      let orders = Object.values(ordersMap);

      const ordersPromises = orders.map((datum: any) =>
        saveOrder(datum, { refetch: false })
      );

      await Promise.all(ordersPromises);
      const getLatestOrderIds = async (rxNumbers: string[]): Promise<Map<string, string>> => {
        const savedOrderIds = new Map<string, string>();
        try {
          for (let i = 0; i < rxNumbers.length; i += 30) {
            const chunk = rxNumbers.slice(i, i + 30);
            const ordersQuery = query(
              collection(db, 'orders'),
              where('rxNumber', 'in', chunk),
              orderBy('createdAt', 'desc')
            );
            const querySnapshot = await getDocs(ordersQuery);
            const processedRxNumbers = new Set();
            querySnapshot.forEach((doc) => {
              const order = doc.data();
              if (order.rxNumber && !processedRxNumbers.has(order.rxNumber)) {
                savedOrderIds.set(order.rxNumber, doc.id);
                processedRxNumbers.add(order.rxNumber);
              }
            });
          }
          return savedOrderIds;
        } catch (error) {
          console.error('Error fetching orders:', error);
          return new Map<string, string>();
        }
      };
      const rxNumbers = (orders as any)
        .map(o => o.rxNumber)
        .filter((rxNumber): rxNumber is string => !!rxNumber);

      const savedOrderIds = await getLatestOrderIds(rxNumbers);


      // The below code is used for sending the sms
      const smsUpdatePromises: Promise<void>[] = [];
      orders.forEach((o: any, index: number) => {
        let patientSaved = savedPatients.find(
          (patient) => patient.id === o.userId
        );
        const orderId = savedOrderIds.get(o.rxNumber);
        //debugger;
        if (!patientSaved) {
          // Patient save failed; skip the text instead of crashing before archiving runs
          console.error(`No saved patient for Rx ${o.rxNumber}, SMS not sent`);
        } else if (patientSaved.phone == undefined && patientSaved.key) {
          patientSaved['phone'] = patientSaved.key.split("|")[3];
          patientSaved['firstName'] = patientSaved.key.split("|")[0];
        }
        if (patientSaved?.phone && o.rxNumber && o.doctor && patientSaved.id && orderId) {
          if (process.env.REACT_APP_ENV === 'prod') {
            sendSms({
              uid: patientSaved.id,
              oid: orderId,
              rxNumber: o.rxNumber,
              drName: o.doctor,
              phone: patientSaved.phone,
              name: patientSaved.firstName,
              sender: senderInfo,
            });
          }
          // Update order with link send tracking
          const linkSentTimestamp = Timestamp.now();
          const updatePromise = updateDoc(doc(db, 'orders', orderId), {
            linkSentAt: linkSentTimestamp,
            linkLastSentAt: linkSentTimestamp,
            linkSendCount: 1
          });
          smsUpdatePromises.push(updatePromise);
        }
        if (index === orders.length - 1) {
          // Wait for all SMS tracking updates to complete
          Promise.all(smsUpdatePromises).then(() => {
            notifications.show({
              title: 'Success',
              message: 'Batch uploaded successfully',
              color: 'green',
              autoClose: 10000,
              closeButton: true
            });
          }).catch((error) => {
            console.error('Error updating link tracking:', error);
            notifications.show({
              title: 'Success',
              message: 'Batch uploaded successfully (with tracking warnings)',
              color: 'yellow',
              autoClose: 10000,
              closeButton: true
            });
          });
        }
      });
    }

    const promises = rowsToSave.map((datum: any) => saveBatchPreview(datum));

    await Promise.all(promises);

    // Archive orders if they came from ProcessPending page
    if (ordersToArchive && ordersToArchive.length > 0) {
      try {
        // Group orders by pendingOrdersId for efficient updates
        const ordersByPendingId = ordersToArchive.reduce((acc, order) => {
          if (!acc[order.pendingOrdersId]) {
            acc[order.pendingOrdersId] = [];
          }
          acc[order.pendingOrdersId].push(order.rxNumber);
          return acc;
        }, {});

        // Update each pending orders document
        const archivePromises = Object.entries(ordersByPendingId).map(async ([pendingOrdersId, rxNumbers]) => {
          const docRef = doc(db, "pharmetika_pending_orders", pendingOrdersId);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const data = docSnap.data();
            const orders = data.orders || [];
            const rxNumbersArray = rxNumbers as string[];

            // Mark the specific orders as archived
            const updatedOrders = orders.map((order: any) => {
              if (rxNumbersArray.includes(order.rxNumber)) {
                return { ...order, archived: true };
              }
              return order;
            });

            // Update the document with the modified orders array
            await updateDoc(docRef, {
              orders: updatedOrders,
            });
          }
        });

        await Promise.all(archivePromises);
        console.log("Successfully archived orders from Process Pending");
        // Without this, Process Pending keeps showing the cached rows as pending and they can be processed again
        invalidatePharmetikaPendingOrdersCache();

        // Update the pending orders count in the sidebar
        updatePendingOrdersCount();
      } catch (error) {
        console.error("Error archiving orders:", error);
        // Don't block the navigation if archiving fails
      }
    }

    setUploading(false);

    // getBatchPreview();
    // getPatients();
    // getOrders();
    navigate(ROUTES.BatchHistory.path);
  };

  const handleCheckboxChange = (e: any, id: string) => {
    const { value, checked } = e.target;
    setBatchPreview((prev: any[]) => {
      let data: any[] = [...prev];
      return data.map((d: any) => {
        if (d.rxNumber == id) {
          const metadata = { ...d.metadata };
          if (checked && SHIPPING_RULE_ACTIONS.includes(value)) {
            SHIPPING_RULE_ACTIONS.forEach((action) => {
              metadata[action] = false;
            });
          }

          return {
            ...d,
            metadata: {
              ...metadata,
              [value]: checked,
            },
          };
        } else return d;
      });
    });
  };

  const handleValueChange = (e: any, id: string) => {
    setBatchPreview((prev: any[]) => {
      let data: any[] = [...prev];
      return data.map((d: any) => {
        if (d.rxNumber == id) {
          return {
            ...d,
            [e.target.name]: e.target.value,
          };
        } else return d;
      });
    });
  };

  const searchFilter = (data: any[], text: string) =>
    data.filter(
      (d) =>
        (d.firstName + " " + d.lastName)
          .toLowerCase()
          .includes(text.toLowerCase()) ||
        moment.utc(d.dateOfBirth)
          .format("MM/DD/YYYY")
          .includes(text.toLowerCase()) ||
        d.species.toLowerCase().includes(text.toLowerCase()) ||
        d.med.toLowerCase().includes(text.toLowerCase()) ||
        d.dosage_form.toLowerCase().includes(text.toLowerCase())
    );

  let filteredBatch = useMemo(() => searchFilter(batchPreview, searchText), [batchPreview, searchText]);

  const getRowIssue = (b: any) => {
    const rx = getRxKey(b);
    if (existingOrders.has(rx)) {
      return `Rx ${rx} already has an order (${describeExistingOrders(existingOrders.get(rx))}), it will be skipped`;
    }
    if (duplicateRxCounts.has(rx)) {
      return `Rx ${rx} is listed ${duplicateRxCounts.get(rx)} times, only the first row will be uploaded`;
    }
    return "";
  };

  const rows = filteredBatch.map((b: any, index: number) => (
    <Table.Tr
      key={b.id || `${b.rxNumber}-${index}`}
      title={getRowIssue(b) || undefined}
      style={getRowIssue(b) ? { backgroundColor: "#fff4e0" } : undefined}>

      <Table.Td>{highlightedText(b.firstName, searchText)}</Table.Td>
      <Table.Td>{highlightedText(b.lastName, searchText)}</Table.Td>
      <Table.Td>{highlightedText(b.species, searchText)}</Table.Td>
      <Table.Td>
        {highlightedText(
          moment.utc(b.dateOfBirth).format("MM/DD/YYYY"),
          searchText
        )}
      </Table.Td>
      <Table.Td>{b.phone}</Table.Td>
      {fromProcessPending && <Table.Td>{b.state || "-"}</Table.Td>}
      <Table.Td>{b.doctor}</Table.Td>
      <Table.Td>{highlightedText(b.med, searchText)}</Table.Td>
      <Table.Td>{highlightedText(b.dosage_form, searchText)}</Table.Td>
      <Table.Td>{b.qty}</Table.Td>
      <Table.Td>{b.price}</Table.Td>
      <Table.Td>{b.memo}</Table.Td>
      <Table.Td>{b.authRefills}</Table.Td>
      <Table.Td>
        {/* <Checkbox
          value={BatchRuleActionType.AltQuantity}
          checked={b?.metadata && b.metadata[BatchRuleActionType.AltQuantity]}
          onChange={(e) => handleCheckboxChange(e, b.rxNumber)}
        /> */}
        <TextInput
          size="sm"
          radius="sm"
          value={b.alt_qty}
          name="alt_qty"
          onChange={(e) => handleValueChange(e, b.rxNumber)}
        />
      </Table.Td>
      <Table.Td>
        {/* <Checkbox
          value={BatchRuleActionType.AltPrice}
          checked={b?.metadata && b.metadata[BatchRuleActionType.AltPrice]}
          onChange={(e) => handleCheckboxChange(e, b.rxNumber)}
        /> */}
        <TextInput
          size="sm"
          radius="sm"
          value={b.alt_price}
          name="alt_price"
          onChange={(e) => handleValueChange(e, b.rxNumber)}
        />
      </Table.Td>
      <Table.Td>
        <Checkbox
          value={BatchRuleActionType.RequireSignature}
          checked={
            b?.metadata && b.metadata[BatchRuleActionType.RequireSignature]
          }
          onChange={(e) => handleCheckboxChange(e, b.rxNumber)}
        />
      </Table.Td>
      <Table.Td>
        <Checkbox
          value={BatchRuleActionType.AutoRefill}
          checked={b?.metadata && b.metadata[BatchRuleActionType.AutoRefill]}
          onChange={(e) => handleCheckboxChange(e, b.rxNumber)}
        />
      </Table.Td>
      <Table.Td>
        <Checkbox
          value={BatchRuleActionType.Overnight}
          checked={b?.metadata && b.metadata[BatchRuleActionType.Overnight]}
          onChange={(e) => handleCheckboxChange(e, b.rxNumber)}
        />
      </Table.Td>
      <Table.Td>
        <Checkbox
          value={BatchRuleActionType.MustUseUPS}
          checked={b?.metadata && b.metadata[BatchRuleActionType.MustUseUPS]}
          onChange={(e) => handleCheckboxChange(e, b.rxNumber)}
        />
      </Table.Td>
      <Table.Td>
        <Checkbox
          value={BatchRuleActionType.UPS2ndDayAirShipping}
          checked={
            b?.metadata && b.metadata[BatchRuleActionType.UPS2ndDayAirShipping]
          }
          onChange={(e) => handleCheckboxChange(e, b.rxNumber)}
        />
      </Table.Td>
      <Table.Td>
        <Checkbox
          value={BatchRuleActionType.CheckForPregnantBreastfeeding}
          checked={
            b?.metadata &&
            b.metadata[BatchRuleActionType.CheckForPregnantBreastfeeding]
          }
          onChange={(e) => handleCheckboxChange(e, b.rxNumber)}
        />
      </Table.Td>

      {/* no payment */}

      <Table.Td>
        <Checkbox
          readOnly
          value={BatchRuleActionType.NoPayment}
          checked={b?.metadata && b.metadata[BatchRuleActionType.NoPayment]}
        />
      </Table.Td>

      {/* Human Flavour */}
      <Table.Td>
        <Checkbox
          readOnly
          value={BatchRuleActionType.HumanMedicineFlavour}
          checked={
            b?.metadata && b.metadata[BatchRuleActionType.HumanMedicineFlavour]
          }
        />
      </Table.Td>

      {/* dog flavour */}
      <Table.Td>
        <Checkbox
          readOnly
          value={BatchRuleActionType.DogMedicineFlavour}
          checked={
            b?.metadata && b.metadata[BatchRuleActionType.DogMedicineFlavour]
          }
        />
      </Table.Td>
      {/* dog treat flavour  */}
      <Table.Td>
        <Checkbox
          readOnly
          value={BatchRuleActionType.DogTreatFlavour}
          checked={
            b?.metadata && b.metadata[BatchRuleActionType.DogTreatFlavour]
          }
        />
      </Table.Td>

      {/* cat flavour  */}
      <Table.Td>
        <Checkbox
          readOnly
          value={BatchRuleActionType.CatMedicineFlavour}
          checked={
            b?.metadata && b.metadata[BatchRuleActionType.CatMedicineFlavour]
          }
        />
      </Table.Td>

      {/* cat treat flavour  */}
      <Table.Td>
        <Checkbox
          readOnly
          value={BatchRuleActionType.CatTreatFlavour}
          checked={
            b?.metadata && b.metadata[BatchRuleActionType.CatTreatFlavour]
          }
        />
      </Table.Td>

      {/* rodent / bird flavour  */}

      <Table.Td>
        <Checkbox
          readOnly
          value={BatchRuleActionType.RodentBirdMedicineFlavour}
          checked={
            b?.metadata &&
            b.metadata[BatchRuleActionType.RodentBirdMedicineFlavour]
          }
        />
      </Table.Td>
    </Table.Tr>
  ));

  const firstRowByRx = new Map<string, any>();
  batchPreview.forEach((b) => {
    const rx = getRxKey(b);
    if (rx && !firstRowByRx.has(rx)) firstRowByRx.set(rx, b);
  });
  const patientName = (rx: string) => {
    const row = firstRowByRx.get(rx);
    return row ? `${row.firstName} ${row.lastName}` : "";
  };
  const existingRxInBatch = Array.from(firstRowByRx.keys()).filter((rx) => existingOrders.has(rx));
  const duplicateRxInBatch = Array.from(duplicateRxCounts.keys()).filter((rx) => !existingOrders.has(rx));
  const skippedRowCount =
    batchPreview.filter((b) => existingOrders.has(getRxKey(b))).length +
    duplicateRxInBatch.reduce((total, rx) => total + (duplicateRxCounts.get(rx) || 1) - 1, 0);

  let batchNotice = null;
  if (!update && batchPreview.length > 0) {
    if (existingRxInBatch.length > 0 || duplicateRxInBatch.length > 0) {
      batchNotice = (
        <Alert color="yellow" variant="light" w={420} title={`${skippedRowCount} row(s) will be skipped on upload`}>
          <Flex direction="column" gap={6} mah={170} style={{ overflowY: "auto" }}>
            {existingRxInBatch.length > 0 && (
              <div>
                <Text size="sm" fw={600}>Already have an order (no new order or text):</Text>
                {existingRxInBatch.map((rx) => (
                  <Text size="sm" key={`existing-${rx}`}>
                    Rx {rx} · {patientName(rx)} · {describeExistingOrders(existingOrders.get(rx))}
                  </Text>
                ))}
                <Text size="xs" c="dimmed" mt={4}>
                  To upload one of these Rx anyway, go to the dashboard (Order Status), archive that Rx, then upload again.
                </Text>
              </div>
            )}
            {duplicateRxInBatch.length > 0 && (
              <div>
                <Text size="sm" fw={600}>Listed more than once (only the first row is uploaded):</Text>
                {duplicateRxInBatch.map((rx) => (
                  <Text size="sm" key={`duplicate-${rx}`}>
                    Rx {rx} · {patientName(rx)} · {duplicateRxCounts.get(rx)} rows
                  </Text>
                ))}
              </div>
            )}
          </Flex>
        </Alert>
      );
    } else if (existingOrdersCheck === "checking") {
      batchNotice = (
        <Alert color="gray" variant="light" w={420} title="Checking for existing orders…" />
      );
    } else if (existingOrdersCheck === "failed") {
      batchNotice = (
        <Alert color="red" variant="light" w={420} title="Couldn't check for existing orders">
          <Text size="sm">Upload will check again and stop if it still can't, so no duplicates are sent.</Text>
        </Alert>
      );
    } else if (existingOrdersCheck === "done") {
      batchNotice = (
        <Alert color="green" variant="light" w={420} title="No duplicates or existing orders found" />
      );
    }
  }

  return (
    <Modal
      opened={open}
      onClose={() => {
        navigate(ROUTES.BatchHistory.path);
      }}
      fullScreen
      transitionProps={{ transition: "slide-up" }}
      styles={{
        header: {
          backgroundColor: colors.bg,
        },
        content: {
          backgroundColor: colors.bg,
        },
      }}>
      <Flex direction={"column"} gap={15}>
        <Flex
          justify={"space-between"}
          mx={15}
          mb={30}
          px={30}
          align={"flex-end"}>
          <Flex align={"center"} justify={"space-between"} w={"100%"} gap={40}>
            <Image w={133} mt={20} h={128} src={assets.images.logo} />
            <Flex direction={"column"} align={"center"} gap={15}>
              <Title>Upload Batch Preview</Title>
              <Input
                w={450}
                className="colored-placeholder"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                }}
                variant="filled"
                size="xl"
                radius={"xl"}
                bg={colors.bg}
                placeholder="Search"
                styles={{
                  input: {
                    background: "white",
                    borderColor: "#6E23CF",
                    borderWidth: "3px",
                  },
                  wrapper: {
                    borderRadius: rem("44px"),
                  },
                }}
              />
            </Flex>
            <Flex w={420} justify="flex-end">
              {batchNotice}
            </Flex>
          </Flex>
        </Flex>

        <Flex mah={"65vh"} style={{ overflowY: "auto", overflowX: "auto" }}>
          <LoadingOverlay visible={loading} />
          <Table
            highlightOnHover
            withTableBorder
            withColumnBorders
            styles={{
              th: {
                height: 100,
              },
            }}>
            <Table.Thead
              bg={colors.bg}
              style={{ position: "sticky", top: 0, zIndex: 999 }}>
              <Table.Tr>
                <Table.Th>FIRST NAME</Table.Th>
                <Table.Th>LAST NAME</Table.Th>
                <Table.Th>SPECIES</Table.Th>
                <Table.Th>DOB</Table.Th>
                <Table.Th>CELL</Table.Th>
                {fromProcessPending && <Table.Th>STATE</Table.Th>}
                <Table.Th>DR</Table.Th>
                <Table.Th>MED</Table.Th>
                <Table.Th>DOSAGE FORM</Table.Th>
                <Table.Th>QTY</Table.Th>
                <Table.Th>PRICE</Table.Th>
                <Table.Th>MEMO</Table.Th>
                <Table.Th>Refill</Table.Th>
                <Table.Th>ALTER QTY</Table.Th>
                <Table.Th>ALT PRICE</Table.Th>
                <RotatedTh>DELIVERY SIG</RotatedTh>
                <RotatedTh>AUTO REFILL</RotatedTh>
                <RotatedTh>OVERNIGHT</RotatedTh>
                <RotatedTh>MUST UPS</RotatedTh>
                <RotatedTh>UPS 2ND DAY</RotatedTh>
                <RotatedTh>PREG / BF</RotatedTh>
                <RotatedTh>No Payment</RotatedTh>
                <RotatedTh>Human Flavor</RotatedTh>
                <RotatedTh>Dog Flavor</RotatedTh>
                <RotatedTh>Dog Treat Flavor</RotatedTh>
                <RotatedTh>Cat Flavor</RotatedTh>
                <RotatedTh>Cat Treat Flavor</RotatedTh>
                <RotatedTh>Rodent Bird Flavor</RotatedTh>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody bg={"white"}>{rows}</Table.Tbody>
          </Table>
        </Flex>

        <div>Rows Count : {rows.length}</div>
        <Button
          loading={uploading}
          onClick={handleUpload}
          style={{ alignSelf: "center" }}
          radius={"xl"}
          size="xl">
          {update ? "Update " : "Upload "}
          Batch
        </Button>
      </Flex>
    </Modal>
  );
}

export default BatchPreview;

const RotatedTh = styled.th`
  transform: rotate(-45deg);
  font-size: 12px;  
`;
